import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LeapV2ProgressReport } from '../models/leap-v2-progress-report.model';
import { LeapV2UploadedFile } from '../models/leap-v2-student.model';
import {
  EvidenceFileDto,
  GetProgressReportsRequest,
  ProgressReportDto,
  UnlockReportRequest,
} from '../models/leap-v2-api.model';
import { BaseService } from '../../../../_services/base/base.service';
import { SessionUserHashManagerService } from '../../../../_services/session-user-hash-manager/session-user-hash-manager.service';
import { environment } from '../../../../../environments/environment';
import { clampSemh, SEMH_MIN } from '../models/leap-v2-semh';
import { toIsoDate } from '../../../../shared/filter-ux/date-presets';

/**
 * Data access for the LEAP v2 6-week Progress Report.
 *
 * Targets the Tutor Portal API ProgressReport controller. Follows the shared
 * BaseService pattern (Bearer + RequestId + RequestMatch headers via
 * prepareOptions; status===299 / HttpErrorResponse guard; handleError).
 */
@Injectable({ providedIn: 'root' })
export class LeapV2ProgressReportService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /** Lists all progress reports for a placement/student (raw DTOs, unordered as the API returns them). */
  listProgressReports(placementId: number, studentId: number): Observable<ProgressReportDto[]> {
    const body: GetProgressReportsRequest = { placementId, studentId };
    return this.http.post(this.apiUrl + 'api/ProgressReport/list', body, this.prepareOptions(body)).pipe(
      map((data: any) => (this.unwrap(data) as ProgressReportDto[]) ?? []),
      catchError(this.handleError('listProgressReports'))
    );
  }

  /**
   * Loads the current progress report for a placement/student. The API returns
   * the collection of reports; the most recent (by report date) is treated as
   * the current one. Returns a blank draft when none exist yet.
   */
  loadProgressReport(placementId: number, studentId: number): Observable<LeapV2ProgressReport> {
    return this.listProgressReports(placementId, studentId).pipe(
      map(reports => {
        const current = this.mostRecent(reports);
        return current ? this.fromDto(current) : this.emptyReport(placementId, studentId);
      })
    );
  }

  /**
   * Loads a single progress report by id (GET /api/ProgressReport/{id}) — used to open a chosen report.
   * loadingPage: the page is loading the report in its URL, so not found reads as one plain message (40973).
   */
  getProgressReportById(id: number, loadingPage: boolean = false): Observable<LeapV2ProgressReport> {
    return this.http.get(this.apiUrl + 'api/ProgressReport/' + id,
      loadingPage ? this.prepareBookingLoadOptions(null) : this.prepareOptions(null)).pipe(
      map((data: any) => this.fromDto(this.unwrap(data) as ProgressReportDto)),
      catchError(loadingPage ? this.handleBookingLoadError('getProgressReportById') : this.handleError('getProgressReportById'))
    );
  }

  /** Builds a blank, save-ready progress report (id 0) for a new entry — no server call. */
  newProgressReport(placementId: number, studentId: number): LeapV2ProgressReport {
    return this.emptyReport(placementId, studentId);
  }

  /** Persists the progress report as a draft. Resolves to the saved report id. */
  saveProgressReport(report: LeapV2ProgressReport): Observable<number> {
    return from(this.toDto(report)).pipe(
      switchMap(dto => this.http.post(this.apiUrl + 'api/ProgressReport/save', dto, this.prepareOptions(dto))),
      map((data: any) => this.unwrap(data) as number),
      catchError(this.handleError('saveProgressReport'))
    );
  }

  /** Submits the progress report for approval. Resolves to the saved report id. */
  submitProgressReport(report: LeapV2ProgressReport): Observable<number> {
    return from(this.toDto(report)).pipe(
      switchMap(dto => this.http.post(this.apiUrl + 'api/ProgressReport/submit', dto, this.prepareOptions(dto))),
      map((data: any) => this.unwrap(data) as number),
      catchError(this.handleError('submitProgressReport'))
    );
  }

  /** Unlocks a submitted report back to draft. */
  /** QA sign-off: Submitted → Approved. Keyed by the report id. */
  authoriseProgressReport(id: number): Observable<boolean> {
    const body = { id: id };
    return this.http.post(this.apiUrl + 'api/ProgressReport/authorise', body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as boolean),
      catchError(this.handleError('authoriseProgressReport'))
    );
  }

  /** QA rejection: Submitted → Rejected. The reason is 20–200 characters and goes back to the tutor. */
  rejectProgressReport(id: number, reason: string): Observable<boolean> {
    const body = { id: id, reason: reason };
    return this.http.post(this.apiUrl + 'api/ProgressReport/reject', body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as boolean),
      catchError(this.handleError('rejectProgressReport'))
    );
  }

  unlockProgressReport(id: number, studentId: number): Observable<boolean> {
    const body: UnlockReportRequest = { id, studentId };
    return this.http.post(this.apiUrl + 'api/ProgressReport/unlock', body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as boolean),
      catchError(this.handleError('unlockProgressReport'))
    );
  }

  // --- mapping ---------------------------------------------------------------

  private fromDto(dto: ProgressReportDto): LeapV2ProgressReport {
    return {
      id: dto.id,
      placementId: dto.placementId,
      studentId: dto.studentId,
      statusId: dto.statusId,
      date: dto.reportDate ? new Date(dto.reportDate) : new Date(),
      approved: dto.approved,
      // The API sends StatusId but no StatusDescription, so the label comes from the id. The Submitted
      // bit stays set once a report is accepted or rejected, so it cannot tell those states apart.
      status: dto.statusDescription ?? progressReportStatusLabel(dto.statusId, dto.submitted, dto.approved),
      assessmentScorePct: dto.assessmentScorePercentage ?? 0,
      // Ratings are 1..5 on the wire; clampSemh also lifts legacy 0-based rows (see leap-v2-semh.ts).
      semh: {
        confidence: clampSemh(dto.confidenceRating),
        resilience: clampSemh(dto.resilienceRating),
        independence: clampSemh(dto.independenceRating),
        motivation: clampSemh(dto.motivationRating),
        selfRegulation: clampSemh(dto.selfRegulationRating),
      },
      progressSummary: dto.last6WeeksSummary ?? '',
      nextPlan: dto.next6WeeksPlan ?? '',
      evidence: (dto.evidence ?? []).map(evidenceFromDto),
      // Deliberately NOT clamped: a missing previous rating must stay null so the comparison can
      // render "—" rather than claiming the learner was rated Very low.
      previous: dto.previous ? {
        fromIlp: dto.previous.fromIlp,
        date: dto.previous.reportDate ? new Date(dto.previous.reportDate) : null,
        assessmentScorePct: dto.previous.assessmentScorePercentage ?? null,
        confidence: dto.previous.confidenceRating ?? null,
        resilience: dto.previous.resilienceRating ?? null,
        independence: dto.previous.independenceRating ?? null,
        motivation: dto.previous.motivationRating ?? null,
        selfRegulation: dto.previous.selfRegulationRating ?? null,
      } : null,
      targetScorePct: dto.targetScorePercentage ?? null,
    };
  }

  private async toDto(report: LeapV2ProgressReport): Promise<ProgressReportDto> {
    return {
      id: report.id ?? 0,
      placementId: report.placementId ?? 0,
      studentId: report.studentId ?? 0,
      statusId: report.statusId,
      // The local calendar date, never toISOString(): the API stores a DATE, and local midnight in BST is
      // 23:00 the day before in UTC, so every save of a loaded report moved it back a day.
      reportDate: toIsoDate(report.date ? new Date(report.date) : new Date()),
      approved: report.approved,
      submitted: report.status === 'Submitted',
      statusDescription: report.status,
      assessmentScorePercentage: report.assessmentScorePct,
      confidenceRating: report.semh.confidence,
      resilienceRating: report.semh.resilience,
      independenceRating: report.semh.independence,
      motivationRating: report.semh.motivation,
      selfRegulationRating: report.semh.selfRegulation,
      last6WeeksSummary: report.progressSummary,
      next6WeeksPlan: report.nextPlan,
      evidence: await buildEvidenceDtos(report.evidence),
      // The list is every file the screen shows, so the API may remove a stored file missing from it (40454).
      evidenceIsComplete: true,
    };
  }

  private emptyReport(placementId: number, studentId: number): LeapV2ProgressReport {
    return {
      id: 0,
      placementId,
      studentId,
      date: new Date(),
      approved: false,
      status: 'Draft',
      assessmentScorePct: 0,
      semh: { confidence: SEMH_MIN, resilience: SEMH_MIN, independence: SEMH_MIN, motivation: SEMH_MIN, selfRegulation: SEMH_MIN },
      progressSummary: '',
      nextPlan: '',
      evidence: [],
      // A blank draft has not been saved, so the API has had no chance to resolve a comparison.
      previous: null,
      targetScorePct: null,
    };
  }

  private mostRecent(reports: ProgressReportDto[]): ProgressReportDto | null {
    if (!reports || reports.length === 0) return null;
    return reports.reduce((latest, r) =>
      new Date(r.reportDate).getTime() > new Date(latest.reportDate).getTime() ? r : latest
    );
  }

  /**
   * Shared body unwrap used by every method — 422 business-validation failures arrive on the
   * error channel and are mapped by BaseService.handleError.
   */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}

/**
 * The screen's status label from the tblStatus id: 1 Draft, 2 Submitted, 3 Approved, 4 Rejected. A row
 * with no id falls back on its approved and submitted flags.
 */
export function progressReportStatusLabel(statusId: number | null | undefined,
  submitted: boolean | null | undefined, approved: boolean | null | undefined): string {
  switch (statusId) {
    case 1: return 'Draft';
    case 2: return 'Submitted';
    case 3: return 'Approved';
    case 4: return 'Rejected';
    default: return approved ? 'Approved' : submitted ? 'Submitted' : 'Draft';
  }
}

/** Maps a loaded evidence DTO to the upload view-model (no in-memory File). */
function evidenceFromDto(dto: EvidenceFileDto): LeapV2UploadedFile {
  return { name: dto.name, size: dto.size, contentType: dto.contentType, url: dto.url };
}

/**
 * Converts upload view-models to evidence DTOs. Newly picked files (carry a
 * native File) are read to base64; files already stored server-side pass
 * through by url. Shared by both LEAP v2 report services.
 */
export function buildEvidenceDtos(files: LeapV2UploadedFile[]): Promise<EvidenceFileDto[]> {
  return Promise.all((files ?? []).map(async (f): Promise<EvidenceFileDto> => {
    if (f.file) {
      return {
        name: f.name,
        size: f.size,
        contentType: f.contentType ?? f.file.type,
        fileBase64: await fileToBase64(f.file),
      };
    }
    return { name: f.name, size: f.size, contentType: f.contentType, url: f.url };
  }));
}

/** Reads a File to a base64 string (payload only, without the data: URL prefix). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.substring(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
